import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  Inventory,
  InventoryApiRow,
  InventoryFilters,
  InventoryListResponse,
  InventoryPayload,
  Pagination,
  SelectOption,
} from "../types";

const STOCKS_URL = "/inventory/stocks";
const MOVEMENTS_URL = "/inventory/movements";

const mapInventory = (row: InventoryApiRow): Inventory => {
  const availableStock = row.availableQuantity || 0;
  const reservedStock = row.reservedQuantity || 0;
  const damagedStock = row.damagedQuantity || 0;

  return {
    id: row.id,
    code: row.code,
    sparePartId: row.sparePartId,
    sparePartName: row.sparePart?.name || "-",
    partCode: row.sparePart?.code || row.sparePart?.partNumber || "-",
    categoryId: row.sparePart?.category?.id,
    categoryName: row.sparePart?.category?.name || "-",
    warehouseId: row.warehouseId,
    warehouseName: row.warehouseId,
    availableStock,
    reservedStock,
    damagedStock,
    totalStock: availableStock + reservedStock + damagedStock,
    minimumStock: row.minimumStock,
    maximumStock: row.maximumStock,
    reorderLevel: row.reorderLevel,
    remarks: row.remarks,
    status: row.status,
    weightedAverageUnitCost: row.weightedAverageUnitCost || 0,
    inventoryValue: row.inventoryValue || 0,
    valuationMethod: row.valuationMethod,
    lastUpdated: row.lastMovementAt
      ? new Date(row.lastMovementAt).toLocaleString()
      : "-",
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
};

class InventoryService {
  async getInventory(params?: InventoryFilters): Promise<InventoryListResponse> {
    const response = await sparePartsApi.get<ApiResponse<InventoryApiRow[]>>(
      STOCKS_URL,
      { params }
    );

    return {
      items: (response.data.data || []).map(mapInventory),
      pagination: response.data.meta as Pagination,
    };
  }

  async getInventoryById(id: string): Promise<Inventory> {
    const response = await sparePartsApi.get<ApiResponse<InventoryApiRow>>(
      `${STOCKS_URL}/${id}`
    );

    return mapInventory(response.data.data);
  }

  async createInventory(payload: InventoryPayload): Promise<Inventory> {
    const response = await sparePartsApi.post<ApiResponse<InventoryApiRow>>(
      STOCKS_URL,
      payload
    );

    return mapInventory(response.data.data);
  }

  async updateInventory(
    id: string,
    payload: InventoryPayload
  ): Promise<Inventory> {
    const response = await sparePartsApi.put<ApiResponse<InventoryApiRow>>(
      `${STOCKS_URL}/${id}`,
      {
        minimumStock: payload.minimumStock,
        maximumStock: payload.maximumStock,
        reorderLevel: payload.reorderLevel,
        remarks: payload.remarks,
      }
    );

    return mapInventory(response.data.data);
  }

  async updateStatus(id: string, status: "ACTIVE" | "INACTIVE"): Promise<void> {
    await sparePartsApi.patch(`${STOCKS_URL}/${id}/status`, { status });
  }

  async deleteInventory(id: string): Promise<void> {
    await sparePartsApi.delete(`${STOCKS_URL}/${id}`);
  }

  async getStockHistory(id: string) {
    const response = await sparePartsApi.get<ApiResponse<unknown[]>>(
      MOVEMENTS_URL,
      { params: { inventoryStockId: id, page: 1, limit: 50 } }
    );

    return response.data.data;
  }

  async getSparePartOptions(): Promise<SelectOption[]> {
    const response = await sparePartsApi.get<ApiResponse<SelectOption[]>>(
      "/spare-parts/dropdown"
    );

    return response.data.data || [];
  }

  async getHubOptions(): Promise<SelectOption[]> {
    const response = await sparePartsApi.get<ApiResponse<SelectOption[]>>(
      "/integrations/hubs"
    );

    return (response.data.data || []).map((hub) => ({
      id: hub.id,
      name: hub.code ? `${hub.name} (${hub.code})` : hub.name,
      code: hub.code,
    }));
  }
}

export default new InventoryService();
